/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

import type { UnityRootState, UnityAppDispatch } from "../UnityAppStore";
import { UnityDialogActionRegistry } from "./UnityDialogActionRegistry";
import type {
  DialogVariant,
  UnityDialogAction,
  UnityDialogActionConfig,
  UnityDialogConfig,
  UnityDialogPayload,
  UnityDialogState
} from "./unityDialog.types";

const DEFAULT_VARIANT: DialogVariant = "info";

const INITIAL_STATE: UnityDialogState = {
  isVisible: false,
  title: "",
  message: "",
  variant: DEFAULT_VARIANT,
  errorCode: "",
  autoHide: false,
  autoHideTimeoutMs: 0,
  onAutoHideActionId: "",
  onBackActionId: "",
  priority: 0,
  actions: [],
  focusedActionIndex: 0
};

const unityDialogSlice = createSlice({
  name: "unityDialog",
  initialState: INITIAL_STATE,
  reducers: {
    /**
     * Show the dialog with the given serializable payload. Internal reducer —
     * callers should dispatch the `showUnityDialog` thunk instead, which
     * additionally handles the non-serializable `onPress` callbacks.
     *
     * Only applies the payload when the dialog is not already visible, or
     * when the incoming priority is greater than or equal to the currently
     * displayed priority — mirrors the `osd` slice's `showOsd` guard so a
     * lower-priority request can never interrupt a higher-priority dialog
     * already on screen. This is a defense-in-depth check: the primary guard
     * lives in the `showUnityDialog` thunk (see below), which also protects
     * `UnityDialogActionRegistry` from being overwritten by a losing request.
     */
    setUnityDialog(state, action: PayloadAction<UnityDialogPayload>): void {
      const payload = action.payload;
      const incomingPriority: number =
        payload.priority !== undefined ? payload.priority : 0;

      if (state.isVisible === true && incomingPriority < state.priority) {
        return;
      }

      state.isVisible = true;
      state.title = payload.title !== undefined ? payload.title : "";
      state.message = payload.message !== undefined ? payload.message : "";
      state.variant =
        payload.variant !== undefined ? payload.variant : DEFAULT_VARIANT;
      state.errorCode =
        payload.errorCode !== undefined ? payload.errorCode : "";
      state.autoHide =
        payload.autoHide !== undefined ? payload.autoHide : false;
      state.autoHideTimeoutMs =
        payload.autoHideTimeoutMs !== undefined ? payload.autoHideTimeoutMs : 0;
      state.onAutoHideActionId =
        payload.onAutoHideActionId !== undefined
          ? payload.onAutoHideActionId
          : "";
      state.onBackActionId =
        payload.onBackActionId !== undefined ? payload.onBackActionId : "";
      state.priority = incomingPriority;
      state.actions = payload.actions;

      const defaultIndex: number =
        payload.defaultFocusedActionId !== undefined
          ? payload.actions.findIndex(
              (action: UnityDialogAction): boolean =>
                action.id === payload.defaultFocusedActionId
            )
          : -1;
      state.focusedActionIndex = defaultIndex >= 0 ? defaultIndex : 0;
    },

    /**
     * Hide the dialog. Does not clear the action registry — see
     * `triggerUnityDialogAction`.
     *
     * Resets `priority` back to 0 so a stale, higher-priority value can't
     * incorrectly block a subsequent lower-priority `showUnityDialog()` call
     * once nothing is visible — mirrors `osd`'s `hideOsd`.
     */
    dismissUnityDialog(state): void {
      state.isVisible = false;
      state.priority = 0;
    },

    /** Reset the dialog state to initial values. */
    resetUnityDialog(state): void {
      state.isVisible = INITIAL_STATE.isVisible;
      state.title = INITIAL_STATE.title;
      state.message = INITIAL_STATE.message;
      state.variant = INITIAL_STATE.variant;
      state.errorCode = INITIAL_STATE.errorCode;
      state.autoHide = INITIAL_STATE.autoHide;
      state.autoHideTimeoutMs = INITIAL_STATE.autoHideTimeoutMs;
      state.onAutoHideActionId = INITIAL_STATE.onAutoHideActionId;
      state.onBackActionId = INITIAL_STATE.onBackActionId;
      state.priority = INITIAL_STATE.priority;
      state.actions = INITIAL_STATE.actions;
      state.focusedActionIndex = INITIAL_STATE.focusedActionIndex;
    },

    /** Move focus to the next action button (LEFT/RIGHT horizontal row). */
    focusNextAction(state): void {
      if (state.focusedActionIndex < state.actions.length - 1) {
        state.focusedActionIndex = state.focusedActionIndex + 1;
      }
    },

    /** Move focus to the previous action button (LEFT/RIGHT horizontal row). */
    focusPrevAction(state): void {
      if (state.focusedActionIndex > 0) {
        state.focusedActionIndex = state.focusedActionIndex - 1;
      }
    },

    /** Directly set the focused action index. Used for pointer hover/focus sync. */
    setFocusedActionIndex(state, action: PayloadAction<number>): void {
      if (action.payload >= 0 && action.payload < state.actions.length) {
        state.focusedActionIndex = action.payload;
      }
    }
  }
});

/* ── Actions ──────────────────────────────────────────────────────────── */
export const {
  setUnityDialog,
  dismissUnityDialog,
  resetUnityDialog,
  focusNextAction,
  focusPrevAction,
  setFocusedActionIndex
} = unityDialogSlice.actions;

/**
 * Reserved registry id under which `UnityDialogConfig.onAutoHide` is stored.
 *
 * Prefixed to keep it out of the caller-facing action-id namespace — a caller's
 * own action can never collide with it, so an auto-hide handler is never
 * reachable from a button press and vice versa.
 */
export const AUTO_HIDE_ACTION_ID: string = "__unityDialogAutoHide";

/**
 * Reserved registry id under which `UnityDialogConfig.onBackPress` is stored.
 * Same rationale as {@link AUTO_HIDE_ACTION_ID} — kept out of the
 * caller-facing action-id namespace.
 */
export const ON_BACK_ACTION_ID: string = "__unityDialogOnBack";

/**
 * showUnityDialog — public thunk used by callers to open the dialog.
 *
 * Splits the caller-facing config into two halves:
 *   1. Non-serializable callbacks — each action's `onPress`, plus `onAutoHide`
 *      under the reserved {@link AUTO_HIDE_ACTION_ID} — captured into
 *      UnityDialogActionRegistry.
 *   2. Serializable `{ id, label }` pairs → dispatched via `setUnityDialog`.
 *
 * Priority guard: when a dialog is already visible, a strictly lower-priority
 * request is rejected BEFORE touching the registry or dispatching anything —
 * unlike the `osd` slice, UnityDialog owns a non-serializable action
 * registry, so checking the guard only inside the `setUnityDialog` reducer
 * would still let a losing request clear/overwrite the currently-visible
 * (winning) dialog's callbacks. Checking here first keeps the registry
 * consistent with whatever dialog is actually on screen.
 *
 * The registry is cleared first so a previous dialog's stale callbacks can
 * never be invoked by a new dialog's action ids.
 */
export const showUnityDialog =
  (config: UnityDialogConfig) =>
  (dispatch: UnityAppDispatch, getState: () => UnityRootState): void => {
    const incomingPriority: number =
      config.priority !== undefined ? config.priority : 0;
    const current = getState().unityDialog;

    if (current.isVisible === true && incomingPriority < current.priority) {
      return;
    }

    UnityDialogActionRegistry.clear();

    const actions: UnityDialogAction[] = config.actions.map(
      (action: UnityDialogActionConfig): UnityDialogAction => {
        if (action.onPress !== undefined) {
          UnityDialogActionRegistry.set(action.id, action.onPress);
        }
        return { id: action.id, label: action.label };
      }
    );

    let onAutoHideActionId: string = "";
    if (config.onAutoHide !== undefined) {
      UnityDialogActionRegistry.set(AUTO_HIDE_ACTION_ID, config.onAutoHide);
      onAutoHideActionId = AUTO_HIDE_ACTION_ID;
    }

    let onBackActionId: string = "";
    if (config.onBackPress !== undefined) {
      UnityDialogActionRegistry.set(ON_BACK_ACTION_ID, config.onBackPress);
      onBackActionId = ON_BACK_ACTION_ID;
    }

    dispatch(
      setUnityDialog({
        title: config.title,
        message: config.message,
        variant: config.variant,
        errorCode: config.errorCode,
        defaultFocusedActionId: config.defaultFocusedActionId,
        autoHide: config.autoHide,
        autoHideTimeoutMs: config.autoHideTimeoutMs,
        onAutoHideActionId: onAutoHideActionId,
        onBackActionId: onBackActionId,
        priority: incomingPriority,
        actions
      })
    );
  };

/**
 * triggerUnityDialogAction — activates the action identified by `actionId`.
 *
 * Shared by both the pointer click handler (UnityDialogContainer) and the
 * D-pad handler (UnityDialogController) so the dismiss → clear → invoke
 * sequence is defined exactly once:
 *   1. Look up the registered callback (if any) for `actionId`.
 *   2. Dismiss the dialog.
 *   3. Clear the registry (prevents stale callbacks leaking to the next dialog).
 *   4. Invoke the callback, if one was registered. A missing callback means
 *      the button is a plain dismiss action (e.g. "Cancel").
 */
export function triggerUnityDialogAction(
  dispatch: UnityAppDispatch,
  actionId: string
): void {
  const callback = UnityDialogActionRegistry.get(actionId);
  dispatch(dismissUnityDialog());
  UnityDialogActionRegistry.clear();
  if (callback !== undefined) {
    callback();
  }
}

/**
 * dismissUnityDialogWithoutAction — closes the dialog without activating any
 * action.
 *
 * Used by the BACK key when the dialog has no `onBackPress` override: BACK
 * then cancels the dialog outright, no action is selected and no `onPress`
 * callback is ever invoked, regardless of which button currently has focus.
 * The registry is still cleared so the dismissed dialog's callbacks cannot
 * leak into the next one.
 */
export function dismissUnityDialogWithoutAction(
  dispatch: UnityAppDispatch
): void {
  dispatch(dismissUnityDialog());
  UnityDialogActionRegistry.clear();
}

/* ── Selectors ────────────────────────────────────────────────────────── */
export const selectIsUnityDialogVisible = (state: UnityRootState): boolean =>
  state.unityDialog.isVisible;

export const selectUnityDialogTitle = (state: UnityRootState): string =>
  state.unityDialog.title;

export const selectUnityDialogMessage = (state: UnityRootState): string =>
  state.unityDialog.message;

export const selectUnityDialogVariant = (
  state: UnityRootState
): DialogVariant => state.unityDialog.variant;

export const selectUnityDialogErrorCode = (state: UnityRootState): string =>
  state.unityDialog.errorCode;

export const selectUnityDialogAutoHide = (state: UnityRootState): boolean =>
  state.unityDialog.autoHide;

export const selectUnityDialogAutoHideTimeoutMs = (
  state: UnityRootState
): number => state.unityDialog.autoHideTimeoutMs;

export const selectUnityDialogOnAutoHideActionId = (
  state: UnityRootState
): string => state.unityDialog.onAutoHideActionId;

export const selectUnityDialogOnBackActionId = (
  state: UnityRootState
): string => state.unityDialog.onBackActionId;

export const selectUnityDialogPriority = (state: UnityRootState): number =>
  state.unityDialog.priority;

export const selectUnityDialogActions = (
  state: UnityRootState
): UnityDialogAction[] => state.unityDialog.actions;

export const selectUnityDialogFocusedActionIndex = (
  state: UnityRootState
): number => state.unityDialog.focusedActionIndex;

export default unityDialogSlice.reducer;
